import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import type { Crm } from "./useCrm";

/**
 * Which CRM the signed-out pages (/auth/login, /auth/signup,
 * /auth/reset-password) dress up as, before there is an organization to ask.
 *
 * In order:
 *   1. `?crm=hubspot|outreach`, which boringplugins.com sends on its links;
 *   2. the Outreach SDK's sign-up popup (`?outreachOrganizationId=`), always Outreach;
 *   3. the choice this browser made last time (`boring.signupCrm`);
 *   4. the CRM the dashboard last showed here (`boring-last-crm`);
 *   5. arriving from one of the website's /hubspot pages (document.referrer);
 *   6. Outreach.
 *
 * A choice from 1, 2 or 5 is saved to both keys, so the next visit and the
 * dashboard's own theme (useCrm, ThemeContext) follow it.
 */

export const SIGNUP_CRM_KEY = "boring.signupCrm";
const LAST_CRM_KEY = "boring-last-crm";

export const asCrm = (value: string | null | undefined): Crm | null => {
  const v = value?.trim().toLowerCase();
  return v === "hubspot" || v === "outreach" ? v : null;
};

const read = (key: string): Crm | null => {
  try {
    return asCrm(window.localStorage.getItem(key));
  } catch {
    return null;
  }
};

const write = (key: string, crm: Crm) => {
  try {
    if (window.localStorage.getItem(key) !== crm) window.localStorage.setItem(key, crm);
  } catch {
    // Private windows: the next page guesses again.
  }
};

const fromReferrer = (): Crm | null => {
  try {
    const ref = document.referrer;
    if (!ref) return null;
    return /\/hubspot(\/|$|\?|#)/.test(new URL(ref).pathname + "/") ? "hubspot" : null;
  } catch {
    return null;
  }
};

/** The CRM the person picked when signing up (or last arrived for), if known. */
export const readSignupCrm = (): Crm | null => read(SIGNUP_CRM_KEY) ?? read(LAST_CRM_KEY);

/** Works out the signed-out pages' CRM from the URL's query string; see above. */
export const detectAuthCrm = (search: string): Crm => {
  const params = new URLSearchParams(search);
  const explicit =
    asCrm(params.get("crm")) ??
    (params.get("outreachOrganizationId") ? "outreach" : null);
  const remembered = explicit ? null : read(SIGNUP_CRM_KEY) ?? read(LAST_CRM_KEY);
  const referred = explicit || remembered ? null : fromReferrer();
  const crm: Crm = explicit ?? remembered ?? referred ?? "outreach";
  if (explicit || referred) {
    write(SIGNUP_CRM_KEY, crm);
    write(LAST_CRM_KEY, crm);
  }
  return crm;
};

export const isAuthPath = (pathname: string): boolean =>
  pathname === "/auth" || pathname.startsWith("/auth/");

/** `path` with `crm=` added, so the CRM survives Login, Signup and Reset links. */
export const withCrm = (path: string, crm: Crm): string => {
  const [base, hash = ""] = path.split("#");
  const [pathname, query = ""] = base.split("?");
  const params = new URLSearchParams(query);
  params.set("crm", crm);
  return `${pathname}?${params.toString()}${hash ? `#${hash}` : ""}`;
};

/** The signed-out pages' CRM for the current URL. */
export const useAuthCrm = (): Crm => {
  const { search } = useLocation();
  return useMemo(() => detectAuthCrm(search), [search]);
};
